import * as moment from 'moment';
import {IChartColorService} from '../../utils/chart-color.service';
import {IDataValueApi} from '../../api/data-value-api.service';
import {IDischargeDataItem} from '../../discharge/data-list/discharge-data-list.controller';
import {IDischargeModel} from '../../api/api.types';
import {IDischargeModelApi} from '../../api/discharge-model-api.service';
import {IDischargeModelItem} from '../../discharge/model-list/model-list.controller';
import {IModelCreatorService} from '../../discharge/model-creator.service';
import {IParsedKN15Telegram} from './kn15-telegram/kn15-parse.service';
import {IParsedTelegramData} from './telegram.controller';
import {List} from 'immutable';
import {OnChanges} from '../../common/common.types';
import hydrologicalYear from '../../utils/hydrological-year';
import {
    ICurveCreatorController,
    ICurveCreatorParent
} from './curve-creator.controller';
import {IDischargeCurveSettings} from '../../discharge/curve.controller';
import {ICurveSettingsApi} from '../../api/curve-settings-api.service';
import {IImomoErrorService} from '../../error/error.service';

export default class OperationalInputCurveController implements OnChanges,
    ICurveCreatorParent {
  static $inject = ['dischargeModelApi', 'chartColorService', 'dataValueApi', 'curveSettingsApi',
    'modelCreatorService', '$scope', '$q', '$rootScope', 'imomoErrorService'];

  /**
   * Input station id.
   */
  stationId:string;
  /**
   * Input telegram which may contain a discharge measurement.
   */
  parsedTelegram:IParsedTelegramData;
  /**
   * Property on parsedTelegram (passed like this to observe changes because onChanges does not perform a deep watch)
   */
  parsedTelegramDate:Date;
  /**
   * Discharge models loaded from the backend.
   */
  dischargeModels:IDischargeModel[];
  /**
   * Discharge models displayed in the discharge model list.
   */
  dischargeModelItems:List<IDischargeModelItem>;
  /**
   * Discharge measurements pulled from the backend.
   */
  loadedDischargeDataItems:List<IDischargeDataItem>;
  /**
   * Discharge measurements to display in the data list.
   */
  measuredDischargeDataItems:List<IDischargeDataItem>;
  /**
   * Selected discharge measurements in the data list.
   */
  selectedDischargeDataItems:List<IDischargeDataItem>;
  /**
   * Minimum water level in the chart.
   */
  minWaterLevel:number;
  /**
   * Maximum water level in the chart.
   */
  maxWaterLevel:number;
  /**
   * Newly created model.
   */
  newModel:IDischargeModel;

  enabledModel:IDischargeModelItem;
  /**
   * Whether the discharge model can be adjusted.
   */
  visibleCurveCreator:boolean;
  /**
   * Callback indicating the selected discharge model.
   */

  dischargeCurveSettings: IDischargeCurveSettings;

  onModelChange:(callbackArgs:{
    dischargeModel:IDischargeModel,
    created:boolean
  })=>void;
  /**
   * Whether there is an async operation pending.
   */
  loadingInProgress:boolean;

  private parsedStationId:number;
  private currentHydrologicalYear:number;
  private curveCreatorController:ICurveCreatorController;

  constructor(private dischargeModelApi:IDischargeModelApi,
              private chartColorService:IChartColorService,
              private dataValueApi:IDataValueApi,
              private curveSettingsApi: ICurveSettingsApi,
              private modelCreatorService:IModelCreatorService,
              private $scope:ng.IScope,
              private $q:ng.IQService,
              private $rootScope: ng.IRootScopeService,
              private imomoErrorService: IImomoErrorService) { 
    this.currentHydrologicalYear = moment(this.parsedTelegramDate).year(); //hydrologicalYear(this.parsedTelegramDate);
    this.measuredDischargeDataItems = List<IDischargeDataItem>();
    this.loadingInProgress = false;

    this.$rootScope.$on('ALL_MODELS_UPDATED', () => {
      this.$onChanges();
      this.$rootScope.$broadcast('MODEL_CHANGE');
    });

  }

  $onChanges() {
    this.resetView();
    this.parsedStationId = Number.parseInt(this.stationId);
    if (!Number.isNaN(this.parsedStationId)) {
      this.loadingInProgress = true;
      this.currentHydrologicalYear = moment(this.parsedTelegramDate).year(); //hydrologicalYear(this.parsedTelegramDate);
      const dischargeModelPromise = this.loadDischargeModels();
      const dischargeDataPromise = this.loadDischargeData();
      this.$q.all([dischargeModelPromise, dischargeDataPromise])
          .finally(() => {
            this.loadingInProgress = false;
          });

      this.curveSettingsApi.getCurveSettings(this.parsedStationId)
          .then(curveSettings => {

            if(curveSettings) {
              this.dischargeCurveSettings = curveSettings[0];
            }

          });
    }
    this.visibleCurveCreator =
        this.parsedTelegram && this.parsedTelegram.dischargeData != null || 
        this.selectedDischargeDataItems && this.selectedDischargeDataItems.size > 1;


  }

  get newModelDate():moment.Moment {
    return this.parsedTelegram && this.parsedTelegram.dischargeData &&
        this.parsedTelegram.dischargeData.date;
  }

  addCurveCreatorController(curveCreatorController:ICurveCreatorController) {
    this.curveCreatorController = curveCreatorController;
  }

  onChartBoundsChange(curveSettings: IDischargeCurveSettings, userChange: boolean) {
    this.minWaterLevel = curveSettings.minWaterLevel;
    this.maxWaterLevel = curveSettings.maxWaterLevel;

    if(userChange && this.parsedStationId) {
      this.curveSettingsApi.createCurveSettings(curveSettings, this.parsedStationId);
    }

  }

  onSelectedDischargeDataItemsChange(selectedDischargeDataItems:List<IDischargeDataItem>) {
    this.selectedDischargeDataItems = selectedDischargeDataItems;
  }

  onNewModelChange(newModel:IDischargeModel, created:boolean) {
    if (created) {
      if (this.newModel) {
        this.dischargeModelItems = this.dischargeModelItems.shift();
      }
      this.dischargeModelItems = this.dischargeModelItems.unshift({
        dischargeModel: newModel,
        enabled: true,
        colors: IChartColorService.BLACK_COLORS
      });
      this.enabledModel.enabled = false;
      this.onModelChange({
        dischargeModel: newModel,
        created: true
      });

      this.dischargeModelApi.createDischargeModel({
          modelName: newModel.modelName,
          paramA: newModel.paramA,
          paramB: newModel.paramB,
          paramC: newModel.paramC,
          paramDeltaLevel: newModel.paramDeltaLevel,
          validFrom: newModel.validFrom ? moment.unix(newModel.validFrom) : null,
          siteId: Number.parseInt(this.stationId)
        })
        .then((dischargeModel) => {
          this.newModel.id = dischargeModel.id;
          this.$rootScope.$broadcast('MODEL_CHANGE');
        })
        .catch((error) => {
          this.imomoErrorService.showErrorDetailsDialog(error.data.details);
        })
    } else { // deleted
      this.dischargeModelItems = this.dischargeModelItems.shift();
      this.enabledModel.enabled = true;
      this.onModelChange({
        dischargeModel: this.enabledModel.dischargeModel,
        created: false
      });

      this.dischargeModelApi.deleteDischargeModel(this.newModel.id).then(() => {
        this.$rootScope.$broadcast('MODEL_CHANGE');
      })
    }
    this.newModel = newModel;
    this.$rootScope.$broadcast('NEW_MODEL_UPDATED')
  }

  private loadDischargeModels() {
    const deferred = this.$q.defer<void>();
    // add last model from the previous year to the list of models in the current year
    this.dischargeModelApi
        .getDischargeModels(this.parsedStationId, (this.currentHydrologicalYear - 1))
        .then(dischargeModelsFromPreviousYear => { 
          const lastDischargeModelFromPreviousYear = dischargeModelsFromPreviousYear.slice(-1);
          
          this.dischargeModelApi
            .getDischargeModels(this.parsedStationId, this.currentHydrologicalYear)
            .then(dischargeModels => {
                let isModelValidInCurrantAndPrevYear = false;
                dischargeModels.forEach((model) => {
                  if (model.id === lastDischargeModelFromPreviousYear[0].id) {
                    isModelValidInCurrantAndPrevYear = true;
                  }
                });
                this.dischargeModels = isModelValidInCurrantAndPrevYear ? dischargeModels : lastDischargeModelFromPreviousYear.concat(dischargeModels);

                this.dischargeModelItems = List(dischargeModels.map(
                    (model, index) => {
                      return {
                        dischargeModel: model,
                        enabled: false,
                        colors: this.chartColorService.getColors(index)
                      };
                    }));
                if (this.dischargeModelItems.size) {
                  let isModelEnabled: boolean = false;
                  this.dischargeModelItems.forEach((dischargeModel: IDischargeModelItem) => { 
                    if (
                      dischargeModel.dischargeModel.validFrom <= moment(this.parsedTelegramDate).unix() && !isModelEnabled
                    ) {
                      dischargeModel.enabled = true;
                      this.enabledModel = dischargeModel;
                      isModelEnabled = true;
                    }
                  });
                  // this.dischargeModelItems.first().enabled = true;
                  //this.dischargeModelItems = List([this.enabledModel])
                  this.onModelChange({
                    dischargeModel: this.enabledModel.dischargeModel,
                    created: false
                  });
                }

                deferred.resolve();
          });
        });

        return deferred.promise;
  }

  private loadDischargeData() {
    const deferred = this.$q.defer<void>();

    // "We want you to save the last 3 points in the Discharge data when switching to the hydrological year
    //     so that we ourselves can form a curve for the new hydrological year at these three points"
    this.dataValueApi.getDischargePairs(this.parsedStationId,
        (this.currentHydrologicalYear - 1))
        .then(dischargePairsFromPrevHydroyear => {
          const lastThreeDischargePairsFromPrevHydroyear = dischargePairsFromPrevHydroyear.slice(0, 3);

          this.dataValueApi.getDischargePairs(this.parsedStationId,
              this.currentHydrologicalYear)
              .then(dischargePairs => {
                dischargePairs = dischargePairs.concat(lastThreeDischargePairsFromPrevHydroyear);

                this.loadedDischargeDataItems = List(dischargePairs
                    .map(dischargeMeasurementPair => {
                      return {
                        date: moment
                            .unix(dischargeMeasurementPair.waterLevel.localDateTime).utc(),
                        waterLevel: dischargeMeasurementPair.waterLevel.dataValue,
                        discharge: dischargeMeasurementPair.discharge.dataValue
                      };
                    }));
                this.updateDischargeDataItems();
                deferred.resolve();
              });
        });

    return deferred.promise;
  }

  private updateDischargeDataItems() {
    this.measuredDischargeDataItems = List(this.loadedDischargeDataItems);
    if (this.parsedTelegram && this.parsedTelegram.dischargeData) {
      const dischargeDataItem:IDischargeDataItem = {
        date: moment(this.parsedTelegram.dischargeData.date),
        waterLevel: this.parsedTelegram.dischargeData.waterLevel,
        discharge: this.parsedTelegram.dischargeData.discharge
      };
      this.measuredDischargeDataItems =
          this.measuredDischargeDataItems.unshift(dischargeDataItem);
    }
  }

  private resetView() {
    this.onModelChange({dischargeModel: null, created: false});
    this.loadedDischargeDataItems = List<IDischargeDataItem>();
    this.measuredDischargeDataItems = List<IDischargeDataItem>();
    this.dischargeModels = [];
    this.dischargeModelItems = List<IDischargeModelItem>();
    this.newModel = null;
    if (this.curveCreatorController) {
      this.curveCreatorController.resetCreatorForm();
    }
  }
}



/** WEBPACK FOOTER **
 ** ./app/operational/input/curve.controller.ts
 **/